import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Badge,
  Button,
  Collapse,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Dashboard,
  People,
  AdminPanelSettings,
  Logout,
  Menu,
  EmailOutlined,
  Phone,
  PhoneMissed,
  Settings,
  Schedule,
  Error as ErrorIcon,
  Cancel,
  ExpandLess,
  ExpandMore,
  NotificationsActive,
  FolderOpen,
  FilterList,
  Assessment,
  Campaign,
  Forum,
  History,
  AutoMode,
} from '@mui/icons-material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useAuthUser, useSignOut } from "react-auth-kit";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  logoutApi,
  getEmailQueueStatusApi,
  getCallQueueStatusApi,
  getCallStatisticsApi,
  getReminderBadgeCountApi,
  getChatConversationsApi,
  getAdminChatConversationsApi,
  signleUsersApi,
} from '../../../Api/Service';
import { clearStoredAvatars, readStoredAvatar, writeStoredAvatar } from "./userAvatarStore";
import { toast } from 'react-toastify';
import { getLiveSocket } from '../../../utils/liveSocket';
import { endLiveSession } from '../../../utils/endLiveSession';
import {
  fetchLeadStatuses,
  getCachedLeadStatuses,
  subscribeLeadStatuses,
} from './components/leadStatusCache';
import { getStatusColor } from './components/leadStatusStyles';
import useLiveCrmUser from './useLiveCrmUser';

const LEADS_LINK = '/admin/dashboard/crm';
let savedSidebarScroll = 0;

const chatClock = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const OnlineDot = ({ online }) => (
  <Box
    component="span"
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: online ? '#22c55e' : '#cbd5e1',
      display: 'inline-block',
      flex: 'none',
    }}
  />
);

const Sidebar = ({ isCollapsed, setIsSidebarCollapsed, isMobileMenu, setisMobileMenu }) => {
  const user = useAuthUser();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerVariant, setDrawerVariant] = useState(isMobile ? "temporary" : "permanent");
  const [emailQueueCount, setEmailQueueCount] = useState(0);
  const [pendingCallsCount, setPendingCallsCount] = useState(0);
  const [failedCallsCount, setFailedCallsCount] = useState(0);
  const [noAnswerCallsCount, setNoAnswerCallsCount] = useState(0);
  const [cancelledCallsCount, setCancelledCallsCount] = useState(0);
  const [reminderBadgeCount, setReminderBadgeCount] = useState(0);
  const [callMenuOpen, setCallMenuOpen] = useState(false);
  const [chatMenuOpen, setChatMenuOpen] = useState(true);
  const [sidebarChats, setSidebarChats] = useState([]);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [leadStatuses, setLeadStatuses] = useState(() => getCachedLeadStatuses() || []);
  const [internalMobileMenu, setInternalMobileMenu] = useState(false);
  const previousPathRef = useRef(location.pathname);
  const menuScrollRef = useRef(null);
  const { can, role, user: liveUser } = useLiveCrmUser();
  const canManageReminders = can("canManageReminders");

  const signedInId = String(user()?.user?._id || "");
  const [profileAvatar, setProfileAvatar] = useState(() => readStoredAvatar(signedInId));

  useEffect(() => {
    const authAvatar = user()?.user?.avatar?.url || (typeof user()?.user?.avatar === "string" ? user()?.user?.avatar : "");
    const liveId = liveUser?._id ? String(liveUser._id) : "";
    const liveAv = liveId && liveId === signedInId
      ? (liveUser?.avatar?.url || (typeof liveUser?.avatar === "string" ? liveUser.avatar : ""))
      : "";
    const next = liveAv || authAvatar || readStoredAvatar(signedInId) || "";
    setProfileAvatar(next);
    if (signedInId) writeStoredAvatar(signedInId, next);

    const onAvatarUpdate = (e) => {
      const updatedId = e.detail?._id ? String(e.detail._id) : signedInId;
      if (updatedId !== signedInId) return;
      const nextAvatar = e.detail?.avatar?.url || (typeof e.detail?.avatar === "string" ? e.detail.avatar : "");
      setProfileAvatar(nextAvatar || "");
      writeStoredAvatar(signedInId, nextAvatar || "");
    };
    window.addEventListener("crm-user-profile-updated", onAvatarUpdate);
    return () => window.removeEventListener("crm-user-profile-updated", onAvatarUpdate);
  }, [liveUser, signedInId]);

  useEffect(() => {
    if (!signedInId) {
      setProfileAvatar("");
      return;
    }
    signleUsersApi(signedInId).then((res) => {
      if (String(user()?.user?._id || "") !== signedInId) return;
      if (res?.success && res.signleUser) {
        const freshAv = res.signleUser.avatar?.url || (typeof res.signleUser.avatar === "string" ? res.signleUser.avatar : "");
        setProfileAvatar(freshAv || "");
        writeStoredAvatar(signedInId, freshAv || "");
      }
    }).catch(() => {});
  }, [signedInId]);

  // Use internal state if setisMobileMenu not provided
  const mobileMenuState = isMobileMenu ?? internalMobileMenu;
  const setMobileMenuState = setisMobileMenu ?? setInternalMobileMenu;

  useEffect(() => {
    const fetchReminderBadge = async () => {
      try {
        const response = await getReminderBadgeCountApi();
        if (response.success) {
          setReminderBadgeCount(response.data?.badgeCount || 0);
        }
      } catch (error) {
        // Non-blocking
      }
    };

    if (!canManageReminders) return undefined;

    fetchReminderBadge();
    const interval = setInterval(fetchReminderBadge, 60000);
    return () => clearInterval(interval);
  }, [canManageReminders]);

  // ✅ Socket.io for real-time email queue updates
  useEffect(() => {
    if (!["superadmin", "admin", "subadmin", "manager"].includes(user()?.user?.role)) return undefined;

    const socket = getLiveSocket();

    const onEmailQueue = (data) => {
      const totalCount = (data.pending || 0) + (data.failed || 0);
      setEmailQueueCount(totalCount);
    };
    socket.on('emailQueueUpdate', onEmailQueue);

    // Fetch initial status
    getEmailQueueStatusApi().then(response => {
      if (response.success) {
        const totalCount = (response.data.pending || 0) + (response.data.failed || 0);
        setEmailQueueCount(totalCount);
      }
    }).catch(err => {
      console.error('Error fetching email queue status:', err);
    });

    return () => {
      socket.off('emailQueueUpdate', onEmailQueue);
    };
  }, [user]);

  const isSuperadmin = role === 'superadmin';
  const hasCrmAccess = can('accessCrm');
  const canAccessCallDashboard = can('canAccessCallDashboard');
  const canAccessChat = can('canAccessChat');

  // ✅ Fetch pending and failed calls counts for badges
  useEffect(() => {
    if (!canAccessCallDashboard) return;

    let inFlight = false;
    const fetchCallCounts = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const [queueRes, statsRes] = await Promise.all([
          getCallQueueStatusApi({ scheduledPage: 1, scheduledLimit: 1 }),
          getCallStatisticsApi()
        ]);

        if (queueRes.success && queueRes.queue?.scheduledPagination) {
          setPendingCallsCount(queueRes.queue.scheduledPagination.total || 0);
        }

        if (statsRes.success && statsRes.statistics?.byStatus) {
          const failedStats = statsRes.statistics.byStatus.find(s => s._id === 'failed');
          if (failedStats) {
            setFailedCallsCount(failedStats.count || 0);
          }
          const noAnswerStats = statsRes.statistics.byStatus.find(s => s._id === 'no-answer');
          if (noAnswerStats) {
            setNoAnswerCallsCount(noAnswerStats.count || 0);
          }
          const cancelledStats = statsRes.statistics.byStatus.find(s => s._id === 'cancelled');
          if (cancelledStats) {
            setCancelledCallsCount(cancelledStats.count || 0);
          }
        }
      } catch (error) {
        console.error('Error fetching call counts:', error);
      } finally {
        inFlight = false;
      }
    };

    fetchCallCounts();
    const interval = setInterval(fetchCallCounts, 60000);
    return () => clearInterval(interval);
  }, [canAccessCallDashboard]);

  const menuItems = [];
  if (hasCrmAccess) {
    menuItems.push({ icon: <People />, label: 'Leads', link: "/admin/dashboard/crm" });
    menuItems.push({ icon: <Assessment />, label: 'Statistics', link: '/admin/crm/list-statistics' });
    if (can('canManageReminders')) {
      menuItems.push({
        icon: <NotificationsActive />,
        label: 'Reminders',
        link: '/admin/crm/reminders',
        badge: true,
        badgeCount: reminderBadgeCount,
      });
    }
    if (can('canManageDocuments')) {
      menuItems.push({
        icon: <FolderOpen />,
        label: 'Document Library',
        link: '/admin/crm/documents',
      });
    }
  }

  // Call-related menu items (will be in dropdown)
  const callMenuItems = [];
  if (canAccessCallDashboard) {
    callMenuItems.push({ icon: <Phone />, label: 'Active Calls', link: "/admin/crm/call-dashboard" });
    callMenuItems.push({ icon: <Schedule />, label: 'Pending Calls Queue', link: "/admin/crm/pending-calls", badge: true, badgeCount: pendingCallsCount });
    callMenuItems.push({ icon: <ErrorIcon />, label: 'Failed Calls', link: "/admin/crm/failed-calls", badge: true, badgeCount: failedCallsCount });
    callMenuItems.push({ icon: <PhoneMissed />, label: 'No Answer Calls', link: "/admin/crm/no-answer-calls", badge: true, badgeCount: noAnswerCallsCount });
    callMenuItems.push({ icon: <Cancel />, label: 'Cancelled Calls', link: "/admin/crm/cancelled-calls", badge: true, badgeCount: cancelledCallsCount });
  }

  useEffect(() => {
    if (!canAccessCallDashboard) {
      setCallMenuOpen(false);
      previousPathRef.current = location.pathname;
      return;
    }

    const isCallRoute = (path) => callMenuItems.some(item => path.startsWith(item.link));
    const currentlyCallRoute = isCallRoute(location.pathname);
    const previouslyCallRoute = isCallRoute(previousPathRef.current);

    if (currentlyCallRoute) {
      setCallMenuOpen(true);
    } else if (previouslyCallRoute) {
      setCallMenuOpen(false);
    }

    previousPathRef.current = location.pathname;
  }, [location.pathname, canAccessCallDashboard, callMenuItems.length]);

  useEffect(() => {
    if (!canAccessChat) return undefined;
    let stopped = false;
    const load = async () => {
      try {
        const response = isSuperadmin ? await getAdminChatConversationsApi() : await getChatConversationsApi();
        if (!stopped && response?.success) setSidebarChats(response.conversations || []);
      } catch (error) {
        if (!stopped) setSidebarChats([]);
      }
    };
    load();
    const timer = setInterval(load, 20000);
    const onChat = (event) => {
      if (event.detail?.event === 'chat.typing') return;
      load();
    };
    window.addEventListener('crm-chat-stream', onChat);
    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener('crm-chat-stream', onChat);
    };
  }, [canAccessChat, isSuperadmin]);
  // if (canAccessAiInstructions) {
  //   menuItems.push({ icon: <Settings />, label: 'AI Instructions', link: "/admin/crm/ai-instructions" });
  // }

  // Admin Management (CRM) - superadmin only
  if (canAccessChat) {
    menuItems.push({ icon: <Forum />, label: isSuperadmin ? 'All chats' : 'Chats', link: isSuperadmin ? '/admin/crm/chat/monitor' : '/admin/crm/chat/chats', chats: true });
  }

  if (isSuperadmin) {
    menuItems.push({ icon: <Campaign />, label: 'Messages', link: "/admin/crm/messages" });
    menuItems.push({ icon: <AdminPanelSettings />, label: 'Admin Management', link: "/admin/crm/admin-management" });
  }

  // Add Recycle Bin and Email Queue for superadmin
  if (user()?.user?.role === 'superadmin') {  // Commented out for now
    menuItems.push({ icon: <AutoMode />, label: 'Automation', link: "/admin/crm/lead-automation" });
    menuItems.push({ icon: <History />, label: 'Upload history', link: "/admin/crm/upload-history" });
    menuItems.push({ icon: <DeleteForeverIcon />, label: 'Recycle Bin', link: "/admin/dashboard/crm/recycle-bin" });
    menuItems.push({ icon: <EmailOutlined />, label: 'Email', link: "/admin/crm/email-queue", badge: true });
  }

  // Add Profile link for all admins and superadmin
  if (can('isProfileUpdate')) {
    menuItems.push({ icon: <Settings />, label: 'My Profile', link: "/admin/crm/profile" });
  }

  const isActiveLink = (link) => {
    if (link === "/admin/crm/upload-history" || link === "/admin/crm/lead-automation") return location.pathname.startsWith(link);
    return location.pathname === link || (link.startsWith("/admin/crm/chat") && location.pathname.startsWith("/admin/crm/chat"));
  };
  const isLeadsRoute = location.pathname === LEADS_LINK;
  const activeStatusFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('status') || '';
  }, [location.search]);

  useEffect(() => {
    if (!hasCrmAccess) return undefined;
    return subscribeLeadStatuses(setLeadStatuses);
  }, [hasCrmAccess]);

  useEffect(() => {
    if (!hasCrmAccess) return;
    fetchLeadStatuses().catch(() => { });
  }, [hasCrmAccess]);

  useEffect(() => {
    if (isLeadsRoute && hasCrmAccess) {
      setStatusMenuOpen(true);
    }
  }, [isLeadsRoute, hasCrmAccess]);

  const handleLogout = async () => {
    try {
      const logout = await logoutApi();
      if (logout.success) {
        // Clear localStorage token only when backend is localhost
        // For production backend, cookies handle authentication
        if (typeof window !== 'undefined') {
          try {
            // Dynamic import to avoid circular dependency
            const { isBackendLocalhost, isElectronApp } = require('../../../config/appConfig');
            // Clear localStorage token for Electron OR localhost (both use localStorage, not cookies)
            if (isElectronApp() || isBackendLocalhost()) {
              localStorage.removeItem('jwttoken');
            }
          } catch (e) {
            // Fallback: check API URL directly
            if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
              localStorage.removeItem('jwttoken');
            }
          }
        }
        clearStoredAvatars();
        signOut();
        endLiveSession();
        navigate("/auth/login/crm");
      } else {
        toast.error(logout.msg);
      }
    } catch (error) {
      toast.error(error.message || "Logout failed");
    }
  };

  const drawerWidth = isCollapsed ? 80 : 280;

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setDrawerVariant("permanent");
        if (setMobileMenuState) {
          setMobileMenuState(false);
        }
      } else {
        setDrawerVariant("temporary");
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initialize on mount

    return () => window.removeEventListener('resize', handleResize);
  }, [setMobileMenuState]);

  useLayoutEffect(() => {
    const node = menuScrollRef.current;
    if (!node) return;
    node.scrollTop = savedSidebarScroll;
  }, [location.pathname]);

  return (
    <Drawer
      variant={drawerVariant}
      open={isMobile ? mobileMenuState : true}
      onClose={() => setMobileMenuState && setMobileMenuState(false)}
      sx={{
        width: { xs: 280, md: drawerWidth },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: { xs: 280, md: drawerWidth },
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          transition: 'width 0.3s ease, transform 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Header / Logo */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {!isCollapsed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>V</Avatar>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Vocaledge
              </Typography>
            </Box>
          )}
          {isCollapsed && (
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, mx: 'auto' }}>V</Avatar>
          )}
          <Box>
            {/* Desktop collapse toggle */}
            <IconButton
              onClick={() => setIsSidebarCollapsed(!isCollapsed)}
              size="small"
              sx={{ color: 'text.secondary', display: { xs: 'none', md: 'inline-flex' } }}
            >
              {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
            {/* Mobile menu toggle */}
            <IconButton
              onClick={() => setMobileMenuState && setMobileMenuState(false)}
              size="small"
              sx={{ color: 'text.secondary', display: { xs: 'inline-flex', md: 'none' } }}
            >
              <Menu />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Menu Items */}
      <Box
        ref={menuScrollRef}
        onScroll={(event) => {
          savedSidebarScroll = event.currentTarget.scrollTop;
        }}
        sx={{ flex: 1, p: 1, overflow: 'auto' }}
      >
        <List>
          {menuItems.map((item, index) => {
            // Insert Call Dashboard dropdown after the first item (Leads)
            const shouldShowCallMenu = index === 0 && canAccessCallDashboard && callMenuItems.length > 0;

            return (
              <React.Fragment key={index}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={item.chats && !isCollapsed ? 'div' : Link}
                    to={item.chats && !isCollapsed ? undefined : item.link}
                    onClick={() => {
                      if (item.chats && !isCollapsed) {
                        setChatMenuOpen((prev) => !prev);
                        return;
                      }
                      if (isMobile && setMobileMenuState) setMobileMenuState(false);
                    }}
                    sx={{
                      borderRadius: 2,
                      color: isActiveLink(item.link) ? 'primary.main' : 'text.secondary',
                      backgroundColor: isActiveLink(item.link) ? 'action.selected' : 'transparent',
                      '&:hover': {
                        backgroundColor: isActiveLink(item.link) ? 'action.selected' : 'action.hover'
                      },
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 'auto',
                        color: 'inherit',
                        mr: isCollapsed ? 0 : 2,
                      }}
                    >
                      {item.badge && (
                        (item.badgeCount !== undefined ? item.badgeCount > 0 : emailQueueCount > 0)
                      ) ? (
                        <Badge
                          badgeContent={item.badgeCount !== undefined ? item.badgeCount : emailQueueCount}
                          color={item.link?.includes('failed') ? 'error' : item.link?.includes('reminders') ? 'primary' : 'warning'}
                          max={99}
                        >
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActiveLink(item.link) ? 600 : 500,
                        }}
                      />
                    )}
                    {!isCollapsed && item.badge && (
                      (item.badgeCount !== undefined ? item.badgeCount > 0 : emailQueueCount > 0)
                    ) && (
                        <Badge
                          badgeContent={item.badgeCount !== undefined ? item.badgeCount : emailQueueCount}
                          color={item.link?.includes('failed') ? 'error' : item.link?.includes('reminders') ? 'primary' : 'warning'}
                          max={99}
                        />
                      )}
                    {!isCollapsed && item.chats && (chatMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                  </ListItemButton>
                </ListItem>

                {item.chats && (
                  <Collapse in={chatMenuOpen && !isCollapsed} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 1, pr: 0.5, pb: 1 }}>
                      {sidebarChats.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
                          No chats yet
                        </Typography>
                      )}
                      {sidebarChats.slice(0, 30).map((chat) => {
                        const mine = !isSuperadmin;
                        const people = mine ? [chat.other].filter(Boolean) : (chat.participants || []);
                        const title = people.map((person) => person.firstName || 'Agent').join(' · ') || 'Chat';
                        const preview = chat.lastActivityKind === "call"
                          ? (chat.lastMessagePreview || "Call")
                          : (mine && chat.lastMessageSenderName
                            ? `${chat.lastMessageSenderName}: ${chat.lastMessagePreview || "No messages yet"}`
                            : (chat.lastMessagePreview || "No messages yet"));
                        const when = chatClock(chat.lastMessageAt || chat.activityAt);
                        const href = isSuperadmin
                          ? `/admin/crm/chat/monitor/${chat._id}`
                          : `/admin/crm/chat/chats/${chat._id}`;
                        const active = location.pathname.includes(String(chat._id));
                        return (
                          <ListItem key={chat._id} disablePadding sx={{ mb: 0.25 }}>
                            <ListItemButton
                              component={Link}
                              to={href}
                              onClick={() => isMobile && setMobileMenuState && setMobileMenuState(false)}
                              sx={{
                                borderRadius: 2,
                                alignItems: 'flex-start',
                                py: 0.75,
                                px: 1.25,
                                color: active ? 'primary.main' : 'text.secondary',
                                backgroundColor: active ? 'action.selected' : 'transparent',
                              }}
                            >
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  {mine && <OnlineDot online={Boolean(people[0]?.online)} />}
                                  <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: chat.unread ? 700 : 600, flex: 1 }}>
                                    {title}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', flex: 'none' }}>{when}</Typography>
                                </Box>
                                <Typography noWrap sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>
                                  {preview}
                                </Typography>
                              </Box>
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                      <Box sx={{ px: 1, pt: 1 }}>
                        <Button
                          component={Link}
                          to={isSuperadmin ? '/admin/crm/chat/monitor' : '/admin/crm/chat/chats'}
                          onClick={() => isMobile && setMobileMenuState && setMobileMenuState(false)}
                          fullWidth
                          variant="contained"
                          size="small"
                          startIcon={<Forum />}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                        >
                          Open all chats
                        </Button>
                      </Box>
                    </List>
                  </Collapse>
                )}

                {item.link === LEADS_LINK && hasCrmAccess && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => {
                          if (isCollapsed) return;
                          setStatusMenuOpen((prev) => {
                            const next = !prev;
                            localStorage.setItem('lead-status-menu-open', JSON.stringify(next));
                            return next;
                          });
                        }}
                        sx={{
                          borderRadius: 2,
                          color: isLeadsRoute ? 'primary.main' : 'text.secondary',
                          backgroundColor: isLeadsRoute ? 'action.selected' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          justifyContent: isCollapsed ? 'center' : 'flex-start',
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 'auto',
                            color: 'inherit',
                            mr: isCollapsed ? 0 : 2,
                          }}
                        >
                          <FilterList />
                        </ListItemIcon>
                        {!isCollapsed && (
                          <>
                            <ListItemText
                              primary="Status"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: isLeadsRoute ? 600 : 500,
                              }}
                            />
                            {statusMenuOpen ? <ExpandLess /> : <ExpandMore />}
                          </>
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={statusMenuOpen && !isCollapsed} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        <ListItem disablePadding sx={{ mb: 0.5, pl: 4 }}>
                          <ListItemButton
                            component={Link}
                            to={LEADS_LINK}
                            onClick={() => isMobile && setMobileMenuState && setMobileMenuState(false)}
                            sx={{
                              borderRadius: 2,
                              color: isLeadsRoute && !activeStatusFilter ? 'primary.main' : 'text.secondary',
                              backgroundColor: isLeadsRoute && !activeStatusFilter ? 'action.selected' : 'transparent',
                              '&:hover': {
                                backgroundColor: isLeadsRoute && !activeStatusFilter ? 'action.selected' : 'action.hover',
                              },
                              px: 2,
                              py: 1.25,
                            }}
                          >
                            <ListItemText
                              primary="All Leads"
                              primaryTypographyProps={{
                                fontSize: '0.8125rem',
                                fontWeight: isLeadsRoute && !activeStatusFilter ? 600 : 500,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        {leadStatuses.map((status) => {
                          const isActive = isLeadsRoute && activeStatusFilter === status.label;
                          return (
                            <ListItem key={status._id} disablePadding sx={{ mb: 0.5, pl: 4 }}>
                              <ListItemButton
                                component={Link}
                                to={`${LEADS_LINK}?status=${encodeURIComponent(status.label)}`}
                                onClick={() => isMobile && setMobileMenuState && setMobileMenuState(false)}
                                sx={{
                                  borderRadius: 2,
                                  color: isActive ? 'primary.main' : 'text.secondary',
                                  backgroundColor: isActive ? 'action.selected' : 'transparent',
                                  '&:hover': {
                                    backgroundColor: isActive ? 'action.selected' : 'action.hover',
                                  },
                                  px: 2,
                                  py: 1.25,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: getStatusColor(status),
                                    mr: 1,
                                    flexShrink: 0,
                                  }}
                                />
                                <ListItemText
                                  primary={status.label}
                                  primaryTypographyProps={{
                                    fontSize: '0.8125rem',
                                    fontWeight: isActive ? 600 : 500,
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                )}

                {/* Call Dashboard Dropdown Menu - Inserted after first item (Leads) */}
                {shouldShowCallMenu && (
                  <>
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => !isCollapsed && setCallMenuOpen(prev => { const next = !prev; localStorage.setItem('call-menu-open', JSON.stringify(next)); return next; })}
                        sx={{
                          borderRadius: 2,
                          color: (callMenuItems.some(item => isActiveLink(item.link))) ? 'primary.main' : 'text.secondary',
                          backgroundColor: (callMenuItems.some(item => isActiveLink(item.link))) ? 'action.selected' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          },
                          justifyContent: isCollapsed ? 'center' : 'flex-start',
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 'auto',
                            color: 'inherit',
                            mr: isCollapsed ? 0 : 2,
                          }}
                        >
                          <Phone />
                        </ListItemIcon>
                        {!isCollapsed && (
                          <>
                            <ListItemText
                              primary="Call Dashboard"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: (callMenuItems.some(item => isActiveLink(item.link))) ? 600 : 500,
                              }}
                            />
                            {callMenuOpen ? <ExpandLess /> : <ExpandMore />}
                          </>
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={callMenuOpen && !isCollapsed} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {callMenuItems.map((item, itemIndex) => {
                          const isActive = isActiveLink(item.link);
                          return (
                            <ListItem key={itemIndex} disablePadding sx={{ mb: 0.5, pl: 4 }}>
                              <ListItemButton
                                component={Link}
                                to={item.link}
                                onClick={() => isMobile && setMobileMenuState && setMobileMenuState(false)}
                                sx={{
                                  borderRadius: 2,
                                  color: isActive ? 'primary.main' : 'text.secondary',
                                  backgroundColor: isActive ? 'action.selected' : 'transparent',
                                  '&:hover': {
                                    backgroundColor: isActive ? 'action.selected' : 'action.hover'
                                  },
                                  px: 2,
                                  py: 1.5,
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 'auto',
                                    color: 'inherit',
                                    mr: 2,
                                  }}
                                >
                                  {item.badge && item.badgeCount > 0 ? (
                                    <Badge
                                      badgeContent={item.badgeCount}
                                      color={item.link?.includes('failed') ? 'error' : item.link?.includes('no-answer') ? 'warning' : 'default'}
                                      max={99}
                                    >
                                      {item.icon}
                                    </Badge>
                                  ) : (
                                    item.icon
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.label}
                                  primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: isActive ? 600 : 500,
                                  }}
                                />
                                {item.badge && item.badgeCount > 0 && (
                                  <Badge
                                    badgeContent={item.badgeCount}
                                    color={item.link?.includes('failed') ? 'error' : item.link?.includes('no-answer') ? 'warning' : 'default'}
                                    max={99}
                                  />
                                )}
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      <Divider />

      {/* Bottom Section - Logout */}
      <Box sx={{ p: 1 }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: 2,
                py: 1.5,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.main'
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'auto',
                  color: 'inherit',
                  mr: isCollapsed ? 0 : 2,
                }}
              >
                <Logout />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* User Profile */}
      <Box sx={{ p: isCollapsed ? 1.5 : 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isCollapsed ? 0 : 2, width: '100%' }}>
          <Avatar
            src={profileAvatar || ""}
            sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.85rem', fontWeight: 700, mx: isCollapsed ? 'auto' : 0 }}
          >
            {`${user()?.user?.firstName?.[0] || ""}${user()?.user?.lastName?.[0] || ""}`.toUpperCase() || 'A'}
          </Avatar>
          {!isCollapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                {user()?.user?.firstName}{" "}{user()?.user?.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                {user()?.user?.email}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
